import { Button, ToastNotifications } from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import React from 'react'
import { FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS } from '../../../../constants'
import { buildRetailerDetails } from '../../../../functions/invoicing/payment-request-resources'
import { validateMerchantAccount } from '../../../../functions/invoicing/validate-merchant-account'
import { isValidCustomer } from '../../../../functions/sale'
import {
  usePaymentManager,
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectSaleCustomer,
  selectSaleHasLineItemFulfilment,
  selectOutstandingBalance,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { PaymentRequestQueryResponse } from '../../../../functions/invoicing/fetch-invoicing-payment-requests'
import { CancelPaymentRequestModalWrapper } from './CancelPaymentRequestModalWrapper'
import { PaymentRequestModalWrapper } from './PaymentRequestModalWrapper'
import { usePaymentRequestCalculations } from './usePaymentRequestCalculations'

interface PaymentRequestButtonProps {
  amountTendered: string // is the amount entered for partial payments on the Payment Panel, if not edited then equals the sale outstanding balance
  paymentRequests: PaymentRequestQueryResponse[]
  onRefetchPaymentRequests: () => Promise<void>
}

export const PaymentRequestButton: React.FC<PaymentRequestButtonProps> = ({
  amountTendered,
  paymentRequests,
  onRefetchPaymentRequests,
}) => {
  const [showPaymentRequestModal, setShowPaymentRequestModal] = React.useState(
    false
  )
  const [showCancelModal, setShowCancelModal] = React.useState(false)
  const [isValidatingMerchant, setIsValidatingMerchant] = React.useState(false)
  const [hasLSPay, setHasLSPay] = React.useState(false)

  const t = useT()
  const hasFulfillmentLineItems = useAppSelector(
    selectSaleHasLineItemFulfilment
  )
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const paymentManager = usePaymentManager()
  const registerManager = useRegisterManager()
  const retailer = useRetailerSettingsManager().get()
  const currentOutlet = registerManager.getActiveOutlet()

  // Calculate pending requests using the payment requests from props
  // this is necessary to determine if the cancel modal should be shown when the button is clicked
  const {
    pendingRequests,
    totalPendingAmountDollars,
    overpaymentThresholdDollars,
  } = usePaymentRequestCalculations(paymentRequests, outstandingBalance)

  React.useEffect(() => {
    const checkHasLSPaymentType = async () => {
      try {
        const hasLSPayPaymentType = await paymentManager.hasLspayPaymentType()
        setHasLSPay(hasLSPayPaymentType)
      } catch {
        setHasLSPay(false)
      }
    }
    checkHasLSPaymentType()
  }, [paymentManager])

  const isFeatureEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS
  )

  const isOutletInUSAOrCanada =
    currentOutlet?.physical_country_id === 'US' ||
    currentOutlet?.physical_country_id === 'CA'

  const showButton =
    isFeatureEnabled &&
    hasFulfillmentLineItems &&
    isOutletInUSAOrCanada &&
    isValidCustomer(saleCustomer) &&
    hasLSPay

  if (!showButton) {
    return null
  }

  const retailerDetails = buildRetailerDetails({ retailer })

  const showMerchantValidationError = () => {
    ToastNotifications.negative(
      t(
        'Unable to process payment requests at the moment, merchant validation failed.',
        {
          _context: 'Payment request merchant validation error',
        }
      )
    )
  }

  const handleButtonClick = async () => {
    setIsValidatingMerchant(true)

    try {
      await validateMerchantAccount(retailerDetails)

      // Check if we should show cancel modal instead
      // Skip cancel modal if amountTendered exceeds the overpayment threshold
      const isOverpaying =
        parseFloat(amountTendered) > overpaymentThresholdDollars

      if (pendingRequests.length > 0 && isOverpaying) {
        setShowCancelModal(true)
      } else {
        setShowPaymentRequestModal(true)
      }
    } catch (error) {
      showMerchantValidationError()
    } finally {
      setIsValidatingMerchant(false)
    }
  }

  return (
    <>
      <div className="vd-mt4">
        <Button
          jumbo
          className="btn--payment-request"
          variant="supplementary"
          onClick={handleButtonClick}
          disabled={isValidatingMerchant}
        >
          <T
            _str={
              isValidatingMerchant
                ? 'Preparing payment request form...'
                : 'Request payment'
            }
            _context="Payment request button label"
          />
        </Button>
      </div>
      <PaymentRequestModalWrapper
        showPaymentRequestModal={showPaymentRequestModal}
        setShowPaymentRequestModal={() => setShowPaymentRequestModal(false)}
        amountTendered={amountTendered}
      />
      <CancelPaymentRequestModalWrapper
        showCancelModal={showCancelModal}
        setShowCancelModal={() => setShowCancelModal(false)}
        numberOfPaymentRequests={pendingRequests.length}
        totalPaymentRequests={totalPendingAmountDollars}
        totalToPay={outstandingBalance}
        onRefetchPaymentRequests={onRefetchPaymentRequests}
      />
    </>
  )
}
